import React, { useEffect, useState } from 'react';
import { db } from '../../firebaseConfig';
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
  arrayUnion,
} from 'firebase/firestore';
import { QRCodeCanvas } from 'qrcode.react';
import { useUser } from '../../contexts/UserContext';
import { CSVLink } from 'react-csv';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './AdminAssetManager.css';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const AdminAssetManager = () => {
  const [assets, setAssets] = useState([]);
  const [editedFields, setEditedFields] = useState({});
  const [issueResponses, setIssueResponses] = useState({});
  const { user: currentUser } = useUser();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!currentUser?.coffee_shop_id) return;

    const fetchAssets = async () => {
      const q = query(
        collection(db, 'assets'),
        where('coffee_shop_id', '==', currentUser.coffee_shop_id)
      );
      const snapshot = await getDocs(q);
      const assetData = await Promise.all(
        snapshot.docs.map(async (docSnap) => {
          const data = { id: docSnap.id, ...docSnap.data() };
          const issueSnap = await getDocs(
            query(collection(db, 'asset_issues'), where('asset_id', '==', data.id))
          );
          data.issues = issueSnap.docs.map((i) => ({ id: i.id, ...i.data() }));
          return data;
        })
      );
      setAssets(assetData);
    };

    fetchAssets();
  }, [currentUser]);

  const handleFieldChange = (id, field, value) => {
    setEditedFields((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };

  const saveAsset = async (id) => {
    const fields = editedFields[id];
    if (!fields) return;
    await updateDoc(doc(db, 'assets', id), {
      ...fields,
      updatedAt: Timestamp.now(),
    });
    alert(t('adminAssetManager.assetUpdated'));
    window.location.reload();
  };

  const deleteAsset = async (id) => {
    if (window.confirm(t('adminAssetManager.confirmDelete'))) {
      await deleteDoc(doc(db, 'assets', id));
      alert(t('adminAssetManager.assetDeleted'));
      window.location.reload();
    }
  };

  const handleResponseChange = (issueId, value) => {
    setIssueResponses({ ...issueResponses, [issueId]: value });
  };

  const submitResponse = async (issueId, assetId) => {
    const responseText = issueResponses[issueId];
    if (!responseText) return;
    const issueRef = doc(db, 'asset_issues', issueId);
    await updateDoc(issueRef, {
      admin_responses: arrayUnion({
        text: responseText,
        timestamp: Timestamp.now(),
      }),
      resolved: true,
      respondedAt: Timestamp.now(),
    });
    alert(t('adminAssetManager.responseSubmitted'));
    window.location.reload();
  };

  const exportToPDF = () => {
    const docPDF = new jsPDF();
    docPDF.text(t('adminAssetManager.assetsReport'), 14, 16);
    const rows = assets.map((a) => [a.serial_number, a.model, a.category, a.location, a.token]);
    docPDF.autoTable({
      head: [[
        t('adminAssetManager.serialNumber'),
        t('adminAssetManager.model'),
        t('adminAssetManager.category'),
        t('adminAssetManager.location'),
        t('adminAssetManager.token')
      ]],
      body: rows,
      startY: 20,
    });
    docPDF.save('assets.pdf');
  };

  const csvData = [
    [
      t('adminAssetManager.serialNumber'),
      t('adminAssetManager.model'),
      t('adminAssetManager.category'),
      t('adminAssetManager.location'),
      t('adminAssetManager.token')
    ],
    ...assets.map((a) => [a.serial_number, a.model, a.category, a.location, a.token]),
  ];

  return (
    <div className="admin-asset-container">
      <h2 className="text-3xl font-bold mb-6">🔧 {t('adminAssetManager.title')}</h2>
      <button
        onClick={() => navigate('/dashboard')}
        className="bg-gray-200 hover:bg-gray-300 text-black font-medium py-2 px-4 rounded mb-4"
      >
        ← {t('adminAssetManager.backToDashboard')}
      </button>
      <div className="asset-actions">
        <CSVLink data={csvData} filename="assets.csv" className="csv-link-iconbutton">
          📤 {t('adminAssetManager.exportCSV')}
        </CSVLink>
        <button onClick={exportToPDF}>📄 {t('adminAssetManager.exportPDF')}</button>
      </div>

      {assets.map((asset) => (
        <div key={asset.id} className="asset-card">
          {['serial_number', 'model', 'category', 'location', 'admin_pin', 'guide_use', 'safety_conditions'].map((field) => (
            <div className="asset-row" key={field}>
              <label>{t(`adminAssetManager.${field}`) || field.replace('_', ' ').toUpperCase()}:</label>
              <input
                type={field === 'admin_pin' ? 'password' : 'text'}
                defaultValue={asset[field]}
                onChange={(e) => handleFieldChange(asset.id, field, e.target.value)}
              />
            </div>
          ))}

          <div className="asset-actions">
            <button onClick={() => saveAsset(asset.id)}>💾 {t('adminAssetManager.save')}</button>
            <button className="delete-btn" onClick={() => deleteAsset(asset.id)}>🗑 {t('adminAssetManager.delete')}</button>
            <button
              onClick={() => navigate(`/assets-reviews/${asset.id}`)}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded shadow"
            >
              🔍 {t('adminAssetManager.viewReviews')}
            </button>
          </div>

          <div className="qr-section">
            <QRCodeCanvas value={`https://loyaltap.flexpayz.com/asset/${asset.token}`} size={96} />
            <p className="text-xs">{t('adminAssetManager.token')}: {asset.token}</p>
            <button className="text-xs1" onClick={() => navigator.clipboard.writeText(`https://loyaltap.flexpayz.com/asset/${asset.token}`)}>
              {t('adminAssetManager.copyLink')}
            </button>
          </div>

          {asset.issues?.length > 0 && (
            <div className="issue-box">
              <h4>🛠 {t('adminAssetManager.reportedIssues')}</h4>
              {asset.issues.map((issue) => (
                <div key={issue.id} className="issue-card">
                  <p>📝 {t('adminAssetManager.message')}: {issue.description || t('adminAssetManager.noMessage')}</p>
                  <p className="timestamp">🕒 {issue.timestamp?.toDate().toLocaleString()}</p>
                  <p>
                    {t('adminAssetManager.status')}: 
                    <span className={issue.resolved ? 'issue-status' : 'issue-pending'}>
                      {issue.resolved ? t('adminAssetManager.resolved') : t('adminAssetManager.pending')}
                    </span>
                  </p>
                  {issue.admin_response && <p>🧠 {t('adminAssetManager.adminResponse')}: {issue.admin_response}</p>}
                  {!issue.resolved && (
                    <div>
                      <textarea
                        placeholder={t('adminAssetManager.writeResponse')}
                        value={issueResponses[issue.id] || ''}
                        onChange={(e) => handleResponseChange(issue.id, e.target.value)}
                      />
                      <button onClick={() => submitResponse(issue.id, asset.id)}>{t('adminAssetManager.respond')}</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default AdminAssetManager;
